import { Pencil, Plus, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";

import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import type { Column } from "@/components/shared/data-table";
import { DataTable } from "@/components/shared/data-table";
import { PageHeader } from "@/components/shared/page-header";
import { SearchableSelect } from "@/components/shared/searchable-select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { NumberInput } from "@/components/ui/number-input";
import { useHasPermission, useMe } from "@/hooks/use-auth";
import { useServerTable } from "@/hooks/use-server-table";
import { useDeleteUnitConversion } from "@/hooks/use-unit-conversions";
import { useUoms } from "@/hooks/use-uoms";
import { useWorkspaceFilterOption } from "@/hooks/use-workspace-filter";
import { useWorkspaces } from "@/hooks/use-workspaces";
import { getErrorMessage } from "@/lib/error-message";
import { queryKeys } from "@/lib/query-keys";
import { convertQuantity } from "@/services/unit-conversion";
import { unitConversionService } from "@/services/unit-conversion";

import { UnitConversionDialog } from "./unit-conversion-dialog";
import { categoryLabel } from "./uoms";

import type { UnitConversion } from "@artisancode/api-types";
import type { ReactNode } from "react";

export function UnitConversions() {
  const canCreate = useHasPermission("unit_conversions.create");
  const canUpdate = useHasPermission("unit_conversions.update_factor");
  const canDelete = useHasPermission("unit_conversions.delete");

  const { mutate: deleteUnitConversion, isPending: isDeleting } =
    useDeleteUnitConversion();

  const { data: uomsData } = useUoms();
  const { data: me } = useMe();
  const { data: workspacesData } = useWorkspaces({ per_page: 100 });
  const workspaces = workspacesData?.items ?? [];

  const uoms = useMemo(() => uomsData?.items ?? [], [uomsData]);

  function unitLabel(unitId: string) {
    const unit = uoms.find((u) => u.id === unitId);
    return unit ? `${unit.name} (${unit.symbol})` : unitId;
  }

  function unitName(unitId: string) {
    return uoms.find((u) => u.id === unitId)?.name ?? unitId;
  }

  function workspaceName(workspaceId: string) {
    return workspaces.find((w) => w.id === workspaceId)?.name ?? "-";
  }

  function unitSymbol(unitId: string) {
    const unit = uoms.find((u) => u.id === unitId);
    return unit?.symbol ?? unitId;
  }

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<UnitConversion | null>(null);
  const [deleting, setDeleting] = useState<UnitConversion | null>(null);

  const workspaceFilter = useWorkspaceFilterOption();
  const table = useServerTable<UnitConversion>({
    queryKey: (params) => queryKeys.unitConversions.list(params),
    fetcher: (params) => unitConversionService.list(params),
    pageSize: 10,
    filterKeys: ["workspace_id"],
  });

  const items = table.loadedItems;

  const [calcQuantity, setCalcQuantity] = useState("1");
  const [calcFromUnitId, setCalcFromUnitId] = useState("");
  const [calcToUnitId, setCalcToUnitId] = useState("");

  // Fall back to the first two loaded units until the user picks explicitly.
  const effectiveFromUnitId = calcFromUnitId || (uoms[0]?.id ?? "");
  const effectiveToUnitId = calcToUnitId || (uoms[1]?.id ?? "");

  const calcResult = useMemo(() => {
    const qty = Number(calcQuantity);
    if (!effectiveFromUnitId || !effectiveToUnitId || Number.isNaN(qty))
      return null;
    return convertQuantity(qty, effectiveFromUnitId, effectiveToUnitId, items);
  }, [calcQuantity, effectiveFromUnitId, effectiveToUnitId, items]);

  function openAdd() {
    setEditing(null);
    setOpen(true);
  }

  function openEdit(item: UnitConversion) {
    setEditing(item);
    setOpen(true);
  }

  const columns: Column<UnitConversion>[] = [
    {
      key: "from",
      label: "Dari (1 satuan)",
      render: (i) => (
        <span className="font-medium">{unitName(i.fromUnitId)}</span>
      ),
    },
    {
      key: "to",
      label: "Ke",
      render: (i) => (
        <span className="font-medium">{unitName(i.toUnitId)}</span>
      ),
    },
    {
      key: "factor",
      label: "Faktor",
      className: "text-right font-mono",
      headerClassName: "text-right",
      render: (i) => i.factor.toLocaleString("id-ID"),
    },
    {
      key: "category",
      label: "Kategori",
      render: (i) => {
        const category = uoms.find((u) => u.id === i.fromUnitId)?.category;
        return category ? (
          <Badge variant="outline">{categoryLabel[category]}</Badge>
        ) : null;
      },
    },
    ...(me?.canSwitchWorkspace
      ? [
          {
            key: "workspace",
            label: "Workspace",
            render: (i: UnitConversion) => (
              <span className="text-sm">{workspaceName(i.workspaceId)}</span>
            ),
          },
        ]
      : []),
  ];

  return (
    <div className="flex flex-col gap-8">
      <PageHeader
        title="Konversi Satuan"
        description="Kelola faktor konversi antar satuan ukur."
        action={
          <Button size="sm" onClick={openAdd} disabled={!canCreate}>
            <Plus className="mr-1 h-4 w-4" />
            Tambah
          </Button>
        }
      />
      <DataTable
        data={table.items}
        loadedData={table.loadedItems}
        columns={columns}
        loading={table.loading}
        filters={workspaceFilter}
        activeFilters={table.activeFilters}
        onFilterChange={table.onFilterChange}
        page={table.page}
        totalPages={table.totalPages}
        totalCount={table.totalCount}
        onPageChange={table.onPageChange}
        hasMore={table.hasMore}
        onLoadMore={table.onLoadMore}
        actions={(item) => (
          <div className="flex items-center gap-1">
            {canUpdate && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => openEdit(item)}
              >
                <Pencil className="h-4 w-4" />
              </Button>
            )}
            {canDelete && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setDeleting(item)}
                className="text-destructive hover:text-destructive hover:bg-destructive/10"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        )}
      />

      <Card>
        <CardHeader>
          <CardTitle>Coba Konversi</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Field label="Jumlah">
              <NumberInput
                decimals={4}
                value={calcQuantity ? Number(calcQuantity) : undefined}
                onChange={(n) => setCalcQuantity(n ? String(n) : "")}
              />
            </Field>
            <Field label="Dari Satuan">
              <SearchableSelect
                value={effectiveFromUnitId}
                onChange={setCalcFromUnitId}
                options={uoms.map((u) => ({
                  value: u.id,
                  label: `${u.name} (${u.symbol})`,
                }))}
                placeholder="Pilih satuan"
              />
            </Field>
            <Field label="Ke Satuan">
              <SearchableSelect
                value={effectiveToUnitId}
                onChange={setCalcToUnitId}
                options={uoms.map((u) => ({
                  value: u.id,
                  label: `${u.name} (${u.symbol})`,
                }))}
                placeholder="Pilih satuan"
              />
            </Field>
          </div>
          <p className="mt-4 text-sm">
            {calcResult === null ? (
              <span className="text-muted-foreground">
                Tidak ada konversi diketahui antara satuan ini.
              </span>
            ) : (
              <span className="font-medium">
                {calcQuantity} {unitSymbol(effectiveFromUnitId)} = {calcResult}{" "}
                {unitSymbol(effectiveToUnitId)}
              </span>
            )}
          </p>
        </CardContent>
      </Card>

      <UnitConversionDialog
        open={open}
        onOpenChange={setOpen}
        editing={editing}
        uoms={uoms}
      />

      <ConfirmDialog
        open={!!deleting}
        onOpenChange={(isOpen) => !isOpen && setDeleting(null)}
        title="Hapus Konversi Satuan"
        description={
          deleting
            ? `Apakah Anda yakin ingin menghapus konversi 1 ${unitLabel(deleting.fromUnitId)} = ${deleting.factor} ${unitLabel(deleting.toUnitId)}? Tindakan ini tidak dapat dibatalkan.`
            : ""
        }
        confirmLabel="Hapus"
        isConfirming={isDeleting}
        onConfirm={() => {
          if (!deleting) return;
          deleteUnitConversion(deleting.id, {
            onSuccess: () => {
              toast.success("Konversi satuan berhasil dihapus.");
              setDeleting(null);
            },
            onError: (err: Error) => {
              toast.error(
                getErrorMessage(err, "Gagal menghapus konversi satuan."),
              );
            },
          });
        }}
      />
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid gap-1.5">
      <Label>{label}</Label>
      {children}
    </div>
  );
}
